import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Chart, ChartConfiguration } from 'chart.js/auto';
import { GetRegistrantsViewItem } from 'edu-application/dist/registration/view-registrants/view-registrants.viewmodel';

@Component({
  selector: 'app-registrants-chart',
  templateUrl: './registrants-chart.component.html',
  styleUrls: ['./registrants-chart.component.scss']
})
export class RegistrantsChartComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('chartCanvas', { static: true }) chartCanvas!: ElementRef<HTMLCanvasElement>;
  @Input() registrants: GetRegistrantsViewItem[] = [];
  
  private chart: Chart | null = null;

  constructor() { }

  ngOnInit(): void {
    this.createChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['registrants'] && !changes['registrants'].firstChange) {
      this.updateChart();
    }
  }

  ngOnDestroy(): void {
    if (this.chart) {
      this.chart.destroy();
    }
  }

  private createChart(): void {
    if (!this.chartCanvas) return;

    const groupData = this.getGroupData();
    
    const config: ChartConfiguration<'doughnut'> = {
      type: 'doughnut',
      data: {
        labels: groupData.labels,
        datasets: [{
          data: groupData.data,
          backgroundColor: [
            '#07429c', '#ac1600', '#34323d', '#22A06B', '#FF9900', 
            '#6098E8', '#FB4848', '#9C27B0', '#FF5722', '#607D8B'
          ],
          hoverBackgroundColor: [
            '#074fbb', '#cb1c01', '#4a4950', '#28b574', '#ffad33',
            '#70a8f8', '#fc5a5a', '#ab47bc', '#ff6633', '#708a9c'
          ],
          borderWidth: 2,
          borderColor: '#ffffff'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              boxWidth: 20,
              padding: 15,
              usePointStyle: true,
              font: {
                size: 12,
                family: "'Inter', sans-serif"
              }
            }
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const label = context.label || '';
                const value = context.parsed;
                const total = groupData.data.reduce((a, b) => a + b, 0);
                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
                return `${label}: ${value} registrants (${percentage}%)`;
              }
            }
          }
        },
        cutout: '45%'
      }
    };

    this.chart = new Chart(this.chartCanvas.nativeElement, config);
  }

  private updateChart(): void {
    if (!this.chart) {
      this.createChart();
      return;
    }

    const groupData = this.getGroupData();
    this.chart.data.labels = groupData.labels;
    this.chart.data.datasets[0].data = groupData.data;
    this.chart.update();
  }

  private getGroupData(): { labels: string[], data: number[] } {
    if (!this.registrants || this.registrants.length === 0) {
      return { labels: [], data: [] };
    }

    // Group registrants by their group/place, with special handling for schools
    const groupCounts = new Map<string, number>();
    
    this.registrants.forEach(registrant => {
      let groupName: string;
      
      // Check if this is a school registration (place name matches school name)
      const placeName = registrant.place?.split(' - ')[0]?.trim() || 'No Group';
      const schoolName = registrant.schoolName?.trim() || '';
      
      if (placeName === schoolName && schoolName) {
        // This is a school registration - group all schools together
        groupName = 'Schools';
      } else {
        // This is a regular place/group registration
        groupName = registrant.place || 'No Group';
      }
      
      groupCounts.set(groupName, (groupCounts.get(groupName) || 0) + 1);
    });

    // Convert to arrays for Chart.js
    const labels = Array.from(groupCounts.keys());
    const data = Array.from(groupCounts.values());

    return { labels, data };
  }
}
